import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Search, Loader2, ExternalLink, Sparkles, Mail, AlertTriangle, Compass, User, Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* --------------------------- Usage banner ---------------------------- */

function UsageBar({ usage }) {
  if (!usage) return null;
  const pct = Math.min(100, usage.percent || 0);
  const barColor =
    usage.level === "exceeded" ? "#ef4444" :
    usage.level === "alert"    ? "#f59e0b" :
    usage.level === "warn"     ? "#f68b1f" : "#7ac143";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="leads-usage-bar">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[11px] font-black uppercase tracking-widest text-slate-400">Monthly lead searches</div>
          <div className="font-bold text-tc-ink mt-0.5">
            {usage.used.toLocaleString()} <span className="text-slate-400 font-normal">/</span> {usage.limit.toLocaleString()} used
            <span className="text-slate-500 text-sm font-normal"> &middot; resets {usage.resets_on}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-black" style={{ color: barColor }}>{Math.round(pct)}%</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">of free tier</div>
        </div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      {usage.level === "alert" && (
        <div className="mt-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="leads-usage-alert">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Heads up — only <strong>{usage.remaining.toLocaleString()}</strong> free searches left this month. Burn carefully until {usage.resets_on}.</span>
        </div>
      )}
      {usage.level === "exceeded" && (
        <div className="mt-3 flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="leads-usage-exceeded">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Monthly free tier hit. New searches are paused until {usage.resets_on}.</span>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- Lead card ----------------------------- */

function LeadCard({ lead, onDraft, draftingKey, savedNames, onToggleSave, savingKey }) {
  const hasContacts = Array.isArray(lead.contacts) && lead.contacts.length > 0;
  const isSaved = savedNames.has(lead.name);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow overflow-hidden"
      data-testid={`lead-card-${lead.name}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-black text-tc-ink text-lg truncate">{lead.name}</div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
            {lead.sector && <span className="px-2 py-0.5 rounded-full bg-slate-100 font-bold uppercase tracking-wide">{lead.sector}</span>}
            {lead.size_signal && <span>&middot; {lead.size_signal}</span>}
            {lead.website && (
              <a
                href={lead.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-tc-orange hover:underline"
                data-testid={`lead-website-${lead.name}`}
              >
                <ExternalLink className="w-3 h-3" /> site
              </a>
            )}
          </div>
        </div>
        {!hasContacts && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => onToggleSave(lead)}
              disabled={savingKey === lead.name}
              data-testid={`lead-save-${lead.name}`}
              title={isSaved ? "Saved — click to remove from your list" : "Save to your list"}
              className={`h-9 w-9 rounded-xl border flex items-center justify-center transition-colors ${
                isSaved
                  ? "bg-tc-cream border-tc-orange text-tc-orange"
                  : "bg-white border-slate-200 text-slate-400 hover:text-tc-orange hover:border-tc-orange"
              } disabled:opacity-60`}
            >
              {savingKey === lead.name
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : (isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />)}
            </button>
            <button
              onClick={() => onDraft(lead, null)}
              disabled={draftingKey === `${lead.name}::__generic__`}
              data-testid={`lead-draft-${lead.name}`}
              className="h-9 px-3 rounded-xl text-white font-bold text-xs flex items-center gap-1.5 hover:brightness-110 disabled:opacity-60"
              style={{ background: "#f68b1f" }}
            >
              {draftingKey === `${lead.name}::__generic__` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              Draft email
            </button>
          </div>
        )}
        {hasContacts && (
          <button
            onClick={() => onToggleSave(lead)}
            disabled={savingKey === lead.name}
            data-testid={`lead-save-${lead.name}`}
            title={isSaved ? "Saved — click to remove from your list" : "Save to your list"}
            className={`shrink-0 h-9 w-9 rounded-xl border flex items-center justify-center transition-colors ${
              isSaved
                ? "bg-tc-cream border-tc-orange text-tc-orange"
                : "bg-white border-slate-200 text-slate-400 hover:text-tc-orange hover:border-tc-orange"
            } disabled:opacity-60`}
          >
            {savingKey === lead.name
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : (isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />)}
          </button>
        )}
      </div>

      <p className="text-sm text-slate-700 mt-3 leading-relaxed">{lead.why_this_fits}</p>

      {hasContacts && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">People mentioned</div>
          <ul className="space-y-2">
            {lead.contacts.map((c, i) => {
              const key = `${lead.name}::${c.name}`;
              return (
                <li key={key} className="flex items-center justify-between gap-2 bg-tc-cream/40 border border-tc-cream rounded-xl px-2.5 py-2 overflow-hidden">
                  <div className="min-w-0 flex-1 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-tc-ink text-sm truncate" data-testid={`lead-contact-name-${lead.name}-${i}`}>{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.title}
                        {c.source_url && (
                          <>
                            {" · "}
                            <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-tc-cyan hover:underline">source</a>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => onDraft(lead, c)}
                    disabled={draftingKey === key}
                    data-testid={`lead-draft-${lead.name}-${i}`}
                    className="shrink-0 h-8 px-2.5 rounded-lg text-white font-bold text-xs flex items-center gap-1 hover:brightness-110 disabled:opacity-60"
                    style={{ background: "#f68b1f" }}
                    title={`Draft email to ${c.name}`}
                  >
                    {draftingKey === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">Draft to </span>{c.name.split(" ")[0]}
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            onClick={() => onDraft(lead, null)}
            disabled={draftingKey === `${lead.name}::__generic__`}
            className="mt-2 text-xs text-slate-500 hover:text-tc-orange inline-flex items-center gap-1 font-semibold disabled:opacity-60"
            data-testid={`lead-draft-generic-${lead.name}`}
          >
            {draftingKey === `${lead.name}::__generic__` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
            Or draft a general outreach to the org
          </button>
        </div>
      )}

      {lead.sources?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">Evidence</div>
          <ul className="space-y-1">
            {lead.sources.slice(0, 4).map((s, i) => (
              <li key={i}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-tc-cyan hover:underline break-all inline-flex items-start gap-1"
                >
                  <ExternalLink className="w-3 h-3 shrink-0 mt-0.5" />
                  <span className="min-w-0">
                    <span className="font-semibold">{s.title || s.url}</span>
                    {s.published_date && <span className="text-slate-400"> &middot; {s.published_date}</span>}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
}

/* ------------------------------- Page -------------------------------- */

const EXAMPLES = [
  "Mid-size healthcare systems in Ohio talking about staff burnout",
  "Independent schools in Texas discussing teacher retention",
  "Nonprofits in California with recent HR director postings",
  "Manufacturing companies hiring culture or engagement leads this quarter",
];

export default function FindLeads() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [timeRange, setTimeRange] = useState("year");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [draftingKey, setDraftingKey] = useState(null);
  const [savedNames, setSavedNames] = useState(new Set());
  const [savingKey, setSavingKey] = useState(null);
  const inputRef = useRef(null);

  const loadUsage = () =>
    api.get("/leads/usage")
      .then(({ data }) => setUsage(data))
      .catch(() => {});

  const loadSavedNames = () =>
    api.get("/saved-leads")
      .then(({ data }) => setSavedNames(new Set((Array.isArray(data) ? data : []).map((l) => l.name))))
      .catch(() => {});

  useEffect(() => {
    loadUsage();
    loadSavedNames();
    inputRef.current?.focus();
  }, []);

  const runSearch = async (q) => {
    const text = (q ?? query).trim();
    if (!text || loading) return;
    if (usage?.level === "exceeded") {
      toast.error("Monthly search limit reached. Try again next month.");
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const { data } = await api.post("/leads/search", { query: text, time_range: timeRange });
      setResults(data);
      setUsage(data.usage);
      if (!data.leads?.length) {
        toast.info("No clear leads matched. Try a more specific query (sector + region + recent signal).");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lead search failed. Try again?");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      runSearch();
    }
  };

  const draftEmail = async (lead, contact) => {
    const key = `${lead.name}::${contact ? contact.name : "__generic__"}`;
    setDraftingKey(key);
    try {
      // 1) create a new email-copilot session
      const { data: session } = await api.post("/email-copilot/sessions");
      // 2) seed CJ with the lead context + target. Build a strong brief so
      // CJ writes a usable first draft (not a generic placeholder).
      const parts = [];
      if (contact) {
        parts.push(
          `Write a single cold outreach email addressed to ${contact.name}, ${contact.title} at ${lead.name}.`
        );
      } else {
        parts.push(
          `Write a single cold outreach email to ${lead.name}` +
          (lead.sector ? ` (${lead.sector})` : "") +
          ` to the most senior HR / People / Culture leader there.`
        );
      }
      if (lead.website) parts.push(`Website: ${lead.website}.`);
      parts.push(`Reason to engage: ${lead.why_this_fits}`);
      if (lead.sources?.length) {
        parts.push(`Reference signals: ${lead.sources.slice(0, 2).map((s) => s.url).join(", ")}`);
      }
      parts.push(`Keep it 120 to 160 words, warm and specific. Use the standard structure and ONE approved CTA. Do not invent prior contact.`);
      const seed = parts.join(" ");

      // Rename session to the org (+ contact name when present)
      const title = contact ? `${lead.name} — ${contact.name}` : `${lead.name} — outreach`;
      await api.patch(`/email-copilot/sessions/${session.id}`, { title }).catch(() => {});
      navigate(`/email-copilot?seed=${encodeURIComponent(seed)}&session=${session.id}`);
    } catch (err) {
      toast.error("Couldn't open CJ for this lead.");
    } finally {
      setDraftingKey(null);
    }
  };

  const toggleSave = async (lead) => {
    const already = savedNames.has(lead.name);
    setSavingKey(lead.name);
    try {
      if (already) {
        // Find id and delete
        const { data: list } = await api.get("/saved-leads");
        const row = (list || []).find((r) => r.name === lead.name);
        if (row) await api.delete(`/saved-leads/${row.id}`);
        setSavedNames((prev) => {
          const next = new Set(prev); next.delete(lead.name); return next;
        });
        toast.success(`Removed ${lead.name} from your list.`);
      } else {
        await api.post("/saved-leads", {
          name: lead.name,
          website: lead.website || null,
          sector: lead.sector || null,
          size_signal: lead.size_signal || null,
          why_this_fits: lead.why_this_fits || "",
          sources: (lead.sources || []).slice(0, 6),
          contacts: (lead.contacts || []).slice(0, 3),
          query: results?.query || null,
        });
        setSavedNames((prev) => new Set(prev).add(lead.name));
        toast.success(`Saved ${lead.name} to your list.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update your saved list.");
    } finally {
      setSavingKey(null);
    }
  };

  const canSearch = usage?.level !== "exceeded";

  return (
    <motion.div
      className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 pb-12 space-y-5"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="find-leads-page"
    >
      <div className="flex items-center justify-between gap-3">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-tc-ink" data-testid="leads-back">
          <ArrowLeft className="w-4 h-4" /> Back to Hub
        </Link>
        <Link
          to="/saved-leads"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-tc-ink hover:text-tc-orange"
          data-testid="leads-view-saved"
        >
          <Bookmark className="w-4 h-4" /> Saved Leads
          {savedNames.size > 0 && (
            <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-tc-orange text-white text-[11px] font-black flex items-center justify-center">{savedNames.size}</span>
          )}
        </Link>
      </div>

      {/* Header + usage */}
      <div className="grid md:grid-cols-[1fr_320px] gap-4 items-start">
        <div>
          <div className="tc-eyebrow flex items-center gap-2 text-slate-400"><Compass size={14} /> Find Leads</div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-tc-ink mt-1">
            Find real orgs that need the True Colors System.
          </h1>
          <p className="text-slate-600 mt-1">
            Describe who you're looking for and CJ scours the live web, filters out noise, and hands you lead cards with real sources.
          </p>
        </div>
        <UsageBar usage={usage} />
      </div>

      {/* Search */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <textarea
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            placeholder="Describe your ideal lead — sector, region, recent signal…"
            disabled={!canSearch || loading}
            data-testid="leads-query-input"
            className="w-full resize-none rounded-xl border-2 border-slate-200 focus:border-tc-orange outline-none pl-10 pr-4 py-3 text-[15px] leading-relaxed"
          />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Signal from</label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            data-testid="leads-time-range"
            className="h-9 rounded-lg border-2 border-slate-200 bg-white px-2 text-sm"
          >
            <option value="month">Last month</option>
            <option value="year">Last year</option>
            <option value="">Any time</option>
          </select>
          <div className="flex-1" />
          <button
            onClick={() => runSearch()}
            disabled={!query.trim() || !canSearch || loading}
            data-testid="leads-run-search"
            className="h-10 px-5 rounded-xl text-white font-bold text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110"
            style={{ background: "#f68b1f" }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Scouring the web…" : "Find leads"}
          </button>
        </div>

        {/* Example chips (hide once we have results) */}
        {!results && !loading && (
          <div className="pt-2 flex flex-wrap gap-2">
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                onClick={() => { setQuery(ex); setTimeout(() => runSearch(ex), 0); }}
                className="text-xs text-slate-600 bg-slate-100 hover:bg-tc-cream border border-slate-200 rounded-full px-3 py-1.5 transition-colors"
                data-testid={`leads-example-${i}`}
              >
                {ex}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Results */}
      {loading && (
        <div className="text-center py-12 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          <div className="text-sm">Searching the web and ranking leads… this can take 15-20 seconds.</div>
        </div>
      )}

      {results && !loading && (
        <div className="space-y-3" data-testid="leads-results">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500">
              <strong className="text-tc-ink">{results.leads.length}</strong> leads from <strong>{results.raw_results_count}</strong> web results
            </div>
            {draftingKey && <div className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> opening CJ…</div>}
          </div>
          {results.leads.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
              No clear leads matched. Try naming a sector + region + recent signal, e.g.
              <br />
              <em>"regional hospitals in the Midwest hiring HR directors this year"</em>.
            </div>
          ) : (
            <div className="grid gap-3">
              {results.leads.map((lead, i) => (
                <LeadCard
                  key={`${lead.name}-${i}`}
                  lead={lead}
                  onDraft={draftEmail}
                  draftingKey={draftingKey}
                  savedNames={savedNames}
                  onToggleSave={toggleSave}
                  savingKey={savingKey}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
